import React from 'react';
import './StudentDetailModal.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

const StudentDetailModal = ({ 
    isOpen, 
    onClose, 
    studentData, 
    studentGeneral = null,
    courseOutcomes, 
    programOutcomes, 
    questions,
    outcomeMappings,
    courseId,
    programId,
    examTypes = [],
    selectedExamType = '',
    onSelectExamType,
    examWeights = []
}) => {
    // Scroll durumu state'i
    const [isScrolled, setIsScrolled] = React.useState(false);
    // (Kaldırıldı: kullanılmayan state'ler)

    // React Hook'ları early return'den önce çağrılmalı
    React.useEffect(() => {
        if (isOpen && studentData) {
            const modalBody = document.querySelector('.student-modal-body');
            if (modalBody) {
                // Scroll event listener
                const handleScroll = () => {
                    const scrollTop = modalBody.scrollTop;
                    const shouldCompact = scrollTop > 30; // 30px aşağı kaydırınca kompakt yap
                    
                    if (shouldCompact !== isScrolled) {
                        setIsScrolled(shouldCompact);
                    }
                };

                modalBody.addEventListener('scroll', handleScroll);
                
                // Cleanup
                return () => {
                    modalBody.removeEventListener('scroll', handleScroll);
                };
            }
        }
    }, [isOpen, studentData, isScrolled]);

    // isOpen kontrolünü JSX içinde yapacağız (hooks sırasını bozmamak için)

    const { studentInfo = [], questionAnalysis = [], answersRaw = [], dc = 0, dcPercent = null } = (studentData || {});
    const selectedType = String(selectedExamType || '').trim().toLowerCase();
    const dataExamType = String((studentInfo && studentInfo[4]) || '').trim().toLowerCase();
    const hasDataForSelectedExam = !selectedType || selectedType === dataExamType;

    // weightedTotalPoints hesaplaması pointsPerQuestionDisplay tanımlandıktan sonra yapılacak

    // (sorular/puan) hesapları questionsForExam tanımlandıktan sonra yapılacak

    // Seçili sınav türünün ağırlığını bul
    function getSelectedExamWeight() {
        if (!selectedExamType || !Array.isArray(examWeights)) return 100;
        const selNorm = String(selectedExamType).trim().toLowerCase();
        const found = examWeights.find(w => String(w.type || w.exam_type).trim().toLowerCase() === selNorm);
        return found ? Number(found.weight) : 100;
    }

    // Sınav türü kanonikleştirme (vize/final/but/quiz/pro)
    const canonExamType = React.useCallback((value) => {
        const v = String(value || '').trim().toLowerCase();
        if (v === 'midterm' || v.includes('vize')) return 'vize';
        if (v.includes('final')) return 'final';
        if (v.includes('but') || v.includes('bütün') || v.includes('butun') || v.includes('bütü') || v.includes('butu') || v.includes('resit')) return 'but';
        if (v.includes('quiz') || v.includes('kısa') || v.includes('kisa')) return 'quiz';
        if (v === 'pro') return 'pro';
        return v;
    }, []);

    // (Kaldırıldı: kullanılmayan applyExamWeight)

    // Aynı sınav türüne tekrar tıklanınca seçimi temizle (genel görünüm)
    const handleToggleExam = (type) => {
        if (!onSelectExamType) return;
        const clicked = String(type);
        const current = String(selectedExamType || '');
        // Sınav türü değişirken seçili tekil sınavı sıfırla.
        setSelectedSpecificExamId('');
        onSelectExamType(clicked === current ? '' : type);
    };

    // Seçili sınav türüne ait soruları döndür (varsa)
    const getQuestionsForSelectedExam = () => {
        try {
            if (!Array.isArray(questions) || questions.length === 0) return [];
            if (!selectedExamType) return questions || [];
            
            // Seçili sınav türünün weight ID'sini bul
            const sel = Array.isArray(examWeights)
                ? examWeights.find(w => String(w.type || w.exam_type).trim().toLowerCase() === String(selectedExamType).trim().toLowerCase())
                : null;
            const selectedWeightId = sel?.id;

            // questions tablosunda sınav bağı varsa ona göre filtrele
            const hasExamBinding = questions.some(q =>
                q?.exam_weight_id != null ||
                q?.exam_type_id != null ||
                q?.weight_id != null
            );

            if (!hasExamBinding || !selectedWeightId) return [];

            return questions.filter(q =>
                String(q?.exam_weight_id ?? q?.exam_type_id ?? q?.weight_id ?? '') === String(selectedWeightId)
            );
            
        } catch (error) {
            return [];
        }
    };

    const questionsForExam = getQuestionsForSelectedExam();

    // Seçili sınav türüne ait soru indekslerini (orijinal questions dizisindeki index) döndür
    const getSelectedQuestionIndexSet = () => {
        const indexSet = new Set();
        try {
            if (!Array.isArray(questions) || questions.length === 0) return indexSet;
            if (!selectedExamType) {
                questions.forEach((_, idx) => indexSet.add(idx));
                return indexSet;
            }
            const sel = Array.isArray(examWeights)
                ? examWeights.find(w => String(w.type || w.exam_type).trim().toLowerCase() === String(selectedExamType).trim().toLowerCase())
                : null;
            const selectedWeightId = sel?.id;
            if (!selectedWeightId) return indexSet;

            const hasExamBinding = questions.some(q =>
                q?.exam_weight_id != null ||
                q?.exam_type_id != null ||
                q?.weight_id != null
            );
            if (!hasExamBinding) return indexSet;

            questions.forEach((q, idx) => {
                const qWeightId = q?.exam_weight_id ?? q?.exam_type_id ?? q?.weight_id;
                if (String(qWeightId ?? '') === String(selectedWeightId)) {
                    indexSet.add(idx);
                }
            });
        } catch (_) {}
        return indexSet;
    };
    const selectedQuestionIndexSet = getSelectedQuestionIndexSet();
    // (Kaldırıldı: kullanılmayan selectedIndexOrderMap)
    const selectedIndexesSorted = (() => {
        try {
            return Array.from(selectedQuestionIndexSet.values()).sort((a,b)=>a-b);
        } catch (_) {
            return [];
        }
    })();

    // Bu modalda sınav türüne göre gerçek soru sayısı: seçili sınav ağırlık ID'sine göre filtrelenmiş sorular
    const totalQuestionsDisplay = hasDataForSelectedExam ? selectedIndexesSorted.length : 0;

    const pointsPerQuestionDisplay = (() => {
        if (!hasDataForSelectedExam) return 0;
        
        // 1) Önce seçili sınav sorularından puan almayı dene
        if (Array.isArray(questionsForExam) && questionsForExam.length > 0) {
            const scores = questionsForExam.map(q => Number(q?.score || 0)).filter(n => !isNaN(n) && n > 0);
            if (scores.length > 0) {
                // Eğer tüm sorular aynı puana sahipse o puanı döndür
                const allSame = scores.every(s => s === scores[0]);
                if (allSame) return scores[0];
                // Farklı puanlar varsa en sık görüleni döndür
                const freq = scores.reduce((m,v)=>{m[v]=(m[v]||0)+1;return m;},{});
                const most = Object.keys(freq).sort((a,b)=>freq[b]-freq[a])[0];
                return Number(most);
            }
        }
        
        // 2) Tüm sorulardan en sık görülen skoru kullan
        if (Array.isArray(questions) && questions.length > 0) {
            const raw = questions.map(q => Number(q?.score || 0)).filter(n => !isNaN(n) && n > 0);
            if (raw.length > 0) {
                const freq = raw.reduce((m,v)=>{m[v]=(m[v]||0)+1;return m;},{});
                const most = Object.keys(freq).sort((a,b)=>freq[b]-freq[a])[0];
                const mostNum = Number(most);
                if (!isNaN(mostNum) && mostNum > 0) return mostNum;
            }
        }
        
        // 3) Backend'ten gelen pointsPerQuestion varsa kullan
        if (studentData && studentData.pointsPerQuestion != null) {
            return Number(studentData.pointsPerQuestion) || 0;
        }
        
        // 4) Toplam/Adet fallback (son çare)
        const totalPts = Number(studentData?.totalPoints || 0);
        const qCount = totalQuestionsDisplay || 1;
        return totalPts / qCount;
    })();

    // Harf notu hesaplayıcı (tek sınav yüzdesine göre)
    

    // Üst sağ rozet ve hızlı gösterge için: seçili tür yoksa genel not; seçili tür varsa bu sınavın ham puanı
    const [dbExamScore, setDbExamScore] = React.useState(null);
    const [computedGeneralScore, setComputedGeneralScore] = React.useState(null);
    const [specificExamOptions, setSpecificExamOptions] = React.useState([]);
    const [selectedSpecificExamId, setSelectedSpecificExamId] = React.useState('');
    // DB'den sınav bazlı notları çek (optik + online) ve genel notu hesapla
    React.useEffect(() => {
        const abortCtrl = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                setDbExamScore(null);
                setComputedGeneralScore(null);
                if (!courseId || !programId) return;
                if (!Array.isArray(examWeights) || examWeights.length === 0) return;

                const student_no = (studentInfo?.[1] || '').toString().trim();
                const student_name = (studentInfo?.[2] || '').toString().trim();
                if (!student_no && !student_name) return;

                const payload = {
                    program_id: programId,
                    course_id: courseId,
                    students: [{ student_no, student_name }]
                };
                const res = await fetch(API_ENDPOINTS.STUDENT_EXAM_SCORES_BATCH, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    signal: abortCtrl.signal
                });
                const data = await res.json();
                if (cancelled || !res.ok) return;

                const key = student_no || student_name.toLowerCase();
                const scoresRow = (data?.scores || {})[key] || {};

                // exam_weight_id -> canonType map
                const idToCanon = {};
                const canonToId = {};
                examWeights.forEach((w) => {
                    const id = w?.id;
                    const canon = canonExamType(w?.type || w?.exam_type);
                    if (id == null || !canon) return;
                    idToCanon[String(id)] = canon;
                    if (!canonToId[canon]) canonToId[canon] = String(id);
                });

                // Type bazlı skorlar
                const byType = {};
                Object.entries(scoresRow || {}).forEach(([k, v]) => {
                    const canon = idToCanon[String(k)];
                    if (!canon) return;
                    const num = Number(v);
                    if (Number.isFinite(num)) byType[canon] = num;
                });

                // Seçili sınav skorunu ayarla
                if (selectedExamType) {
                    const selCanon = canonExamType(selectedExamType);
                    const val = Number(byType[selCanon]);
                    setDbExamScore(Number.isFinite(val) ? val : null);
                }

                // Genel ağırlıklı hesap (büt finali değiştirir)
                const weightsMap = {};
                examWeights.forEach((w) => {
                    const canon = canonExamType(w.type || w.exam_type);
                    weightsMap[canon] = Number(w.weight || 0);
                });
                const hasBut = typeof byType['but'] === 'number' && byType['but'] > 0;
                const finalWeight = Number(weightsMap['final'] || 0);
                let total = 0;
                Object.keys(weightsMap).forEach((t) => {
                    const wt = Number(weightsMap[t] || 0);
                    const pts = Number(byType[t] != null ? byType[t] : 0);
                    if (!Number.isFinite(pts)) return;
                    if (hasBut && t === 'final') return;
                    if (hasBut && t === 'but') {
                        if (finalWeight > 0) total += pts * (finalWeight / 100);
                        return;
                    }
                    if (wt > 0) total += pts * (wt / 100);
                });
                if (Number.isFinite(total)) setComputedGeneralScore(Number(total.toFixed(2)));
            } catch (_) {
                // ignore
            }
        })();

        return () => {
            cancelled = true;
            abortCtrl.abort();
        };
    }, [courseId, programId, studentInfo, examWeights, selectedExamType, canonExamType]);

    // Seçili sınav türü için tekil sınav listesini getir (Quiz 1/2/3 vb.)
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setSpecificExamOptions([]);
                setSelectedSpecificExamId('');
                if (!selectedExamType || !courseId) return;
                const student_no = (studentInfo?.[1] || '').toString().trim();
                const student_name = (studentInfo?.[2] || '').toString().trim();
                if (!student_no && !student_name) return;

                const params = new URLSearchParams({
                    course_id: String(courseId),
                    exam_type: String(selectedExamType || '')
                });
                if (programId) params.set('program_id', String(programId));
                if (student_no) params.set('student_no', student_no);
                if (student_name) params.set('student_name', student_name);

                const res = await fetch(`${API_ENDPOINTS.STUDENT_EXAMS_BY_TYPE}?${params.toString()}`);
                const data = await res.json();
                if (!res.ok || cancelled) return;
                const exams = Array.isArray(data?.exams) ? data.exams : [];
                setSpecificExamOptions(exams);
                // Kullanıcı isteği: sınav türü seçilince önce "Genel" açık gelsin,
                // tekil sınav kullanıcı tarafından sonradan seçilsin.
                setSelectedSpecificExamId('');
            } catch (_) {
                if (!cancelled) {
                    setSpecificExamOptions([]);
                    setSelectedSpecificExamId('');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [selectedExamType, courseId, programId, studentInfo]);

    // İki ayrı not: sol (seçili sınav), sağ (genel)
    const generalScore = (() => {
        if (selectedSpecificExamId) {
            const picked = (specificExamOptions || []).find(
                (x) => String(x?.exam_id) === String(selectedSpecificExamId)
            );
            const v = Number(picked?.score);
            if (Number.isFinite(v)) return v;
        }
        if (Number.isFinite(computedGeneralScore)) return Number(computedGeneralScore);
        const val = Number(studentGeneral);
        if (Number.isFinite(val) && val >= 0) return val;
        const dcP = Number(dcPercent);
        if (Number.isFinite(dcP) && dcP >= 0) return dcP;
        return null;
    })();
    // Precomputed verilerde DB/aggregated yerine yerel hesaplama kullan
    // Pretest modalı tüm sekmelerde (Öntest, Ara Sınav vb.) examWeights içinde id:'pretest' olur
    const usingPrecomputed = !!(
        studentData?.learningOutcomeResults?.length > 0 &&
        Array.isArray(examWeights) &&
        examWeights.some(w => w.id === 'pretest')
    );

    const examScore = (() => {
        if (!selectedExamType) return null;
        // Tekil sınav seçildiyse o sınav notunu göster
        if (selectedSpecificExamId) {
            const picked = (specificExamOptions || []).find(
                (x) => String(x?.exam_id) === String(selectedSpecificExamId)
            );
            const v = Number(picked?.score);
            if (Number.isFinite(v)) return v;
        }
        // Öntest (online sınav) modunda dcPercent (yüzde) kullan
        if (usingPrecomputed && String(selectedExamType).trim().toLowerCase() === 'öntest') {
            const pre = Number(dcPercent);
            return Number.isFinite(pre) ? pre : null;
        }
        if (Number.isFinite(dbExamScore)) return dbExamScore;
        try {
            // Soru puanlarına göre toplam puan hesapla
            let totalPoints = 0;
            const indexes = selectedIndexesSorted;
            for (let i = 0; i < indexes.length; i += 1) {
                const qIdx = indexes[i];
                const ans = answersRaw[qIdx];
                if (!ans) continue;
                const s = String(ans);
                const isAlpha = s && s === s.toUpperCase() && s !== s.toLowerCase();
                if (isAlpha) {
                    // Doğru cevap: sorunun puanını ekle
                    const question = questions[qIdx];
                    const questionScore = Number(question?.score || 0);
                    if (questionScore > 0) {
                        totalPoints += questionScore;
                    } else {
                        // Eğer soru puanı yoksa, pointsPerQuestionDisplay kullan
                        totalPoints += (Number(pointsPerQuestionDisplay) || 0);
                    }
                }
            }
            return totalPoints;
        } catch (_) {
            // Fallback: eski yöntem
            return Number(dc) * (Number(pointsPerQuestionDisplay) || 0);
        }
    })();

    const formatScoreDisplay = React.useCallback((value) => {
        return Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '-';
    }, []);
    const toDisplayOutcomeRate = React.useCallback((rate) => {
        const r = Number(rate || 0);
        if (!Number.isFinite(r)) return 0;
        // ÖÇ/PÇ yüzdesi 0-100 aralığında kalır. Sınav genel notuyla
        // kapatmıyoruz: öğrenci 60 almış olsa bile belirli bir ÖÇ'yi ölçen
        // sorulardan %100 alabilir; yüzde bağımsızdır.
        return Math.max(0, Math.min(r, 100));
    }, []);
    // (Kaldırıldı: kullanılmayan harf notu değişkenleri)

    // Dersin öğrenme çıktıları başarı analizi - Hazır veriler varsa onları kullan, yoksa hesapla
    const calculateLearningOutcomes = () => {
        // Eğer seçili sınav türü için modalın ana datası uyuşmuyor olabilir (online/optik kaynak farkı).
        // Bu durumda DB'den sınav bazlı ÖÇ yüzdeleri geldiyse yine de göster.
        const hasDbLearningForSelected = !!(
            selectedExamType &&
            Array.isArray(dbLearningIds) &&
            Array.isArray(dbLearningPercents) &&
            dbLearningIds.length > 0
        );
        // Ortak motor sonucu olmayan normal sınavı %0 gibi gösterme. Öntest
        // verisi puanlarıyla birlikte hazırlanmışsa aşağıdaki puan-ağırlıklı
        // yerel yol kullanılabilir.
        if (selectedExamType && !hasDbLearningForSelected && !usingPrecomputed) {
            return [];
        }
        if (!hasDataForSelectedExam && !hasDbLearningForSelected && !(studentData?.learningOutcomeResults?.length > 0)) {
            return [];
        }
        // Sınav bazlı DB ÖÇ yüzdeleri geldiyse onları doğrudan kullan
        if (
            selectedExamType &&
            Array.isArray(courseOutcomes) &&
            courseOutcomes.length > 0 &&
            Array.isArray(dbLearningIds) &&
            Array.isArray(dbLearningPercents) &&
            dbLearningIds.length > 0
        ) {
            return courseOutcomes.map((outcome) => {
                const idx = dbLearningIds.indexOf(String(outcome.id));
                const rate = idx >= 0 ? Number(dbLearningPercents[idx]) : 0;
                return {
                    ...outcome,
                    correctCount: 0,
                    totalCount: 0,
                    successRate: Number.isFinite(rate) ? rate : 0,
                    relatedQuestions: []
                };
            });
        }
        // Genel görünümde ağırlıklı sınav birleşimi hazırlandıysa onu kullan
        if (
            !selectedExamType &&
            Array.isArray(courseOutcomes) &&
            courseOutcomes.length > 0 &&
            Array.isArray(aggregatedLearningPercents)
        ) {
            return courseOutcomes
                .map((outcome, idx) => {
                    const rate = Number(aggregatedLearningPercents[idx]);
                    if (!Number.isFinite(rate)) return null;
                    return {
                        ...outcome,
                        correctCount: 0,
                        totalCount: 0,
                        successRate: rate,
                        relatedQuestions: []
                    };
                })
                .filter(Boolean);
        }
        // Genel görünümde ortak motor cevabı gelmeden yerel/legacy yüzde üretme.
        if (!selectedExamType) return [];

        // Eski hesaplama mantığı (fallback)
        if (!courseOutcomes || !questions || !questionAnalysis) {
            return [];
        }
        
        return courseOutcomes.map((outcome) => {
            
            // İlgili soruları ve cevaplarını detaylı olarak topla
            let relatedQuestionsWithAnswers = selectedIndexesSorted
                .map((qIdx, orderIdx) => {
                    const q = questions[qIdx];
                    // topics alanını kontrol et - hem topics hem learning_outcomes destekle
                    const loIds = q.topics || q.learning_outcomes || [];
                    const hasTopicMatch = Array.isArray(loIds) && loIds.some(loId => String(loId) === String(outcome.id));
                    if (!hasTopicMatch) return null;
                    const rawAns = answersRaw[qIdx];
                    const ansStr = rawAns != null ? String(rawAns) : '';
                    const isCorrect = Boolean(ansStr) && ansStr === ansStr.toUpperCase() && ansStr !== ansStr.toLowerCase();
                    // Soru puanını al
                    const questionScore = Number(q?.score || 0) || (Number(pointsPerQuestionDisplay) || 0);
                    return {
                        questionNumber: orderIdx + 1,
                        questionText: q.text || `Soru ${orderIdx + 1}`,
                        studentAnswer: ansStr || 'Boş',
                        isCorrect,
                        correctAnswer: q.correct_answer || 'N/A',
                        questionIndex: qIdx,
                        questionScore: questionScore
                    };
                })
                .filter(Boolean);
            // Kullanıcı isteği: Bu listede numaralar 1..N çift boşluk olmadan ilerlesin
            relatedQuestionsWithAnswers = relatedQuestionsWithAnswers.map((item, i) => ({
                ...item,
                questionNumber: i + 1,
                questionText: item.questionText?.replace(/Soru\s+\d+/, `Soru ${i + 1}`)
            }));
            
            // Soru puanlarına göre başarı oranını hesapla
            let totalPoints = 0;
            let earnedPoints = 0;
            relatedQuestionsWithAnswers.forEach(q => {
                const qScore = q.questionScore || 0;
                totalPoints += qScore;
                if (q.isCorrect) {
                    earnedPoints += qScore;
                }
            });
            const successRate = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
            
            // Doğru cevap sayısı ve toplam soru sayısı (geriye uyumluluk için)
            const correctCount = relatedQuestionsWithAnswers.filter(q => q.isCorrect).length;
            const totalCount = relatedQuestionsWithAnswers.length;
            
            return {
                ...outcome,
                correctCount,
                totalCount,
                successRate: parseFloat(successRate.toFixed(2)),
                relatedQuestions: relatedQuestionsWithAnswers // Detaylı soru bilgileri
            };
        });
    };

    // Program çıktıları başarı analizi - Hazır veriler varsa onları kullan, yoksa hesapla
    const calculateProgramOutcomes = () => {
        const hasDbProgramForSelected = !!(
            selectedExamType &&
            Array.isArray(dbProgramIds) &&
            Array.isArray(dbProgramPercents) &&
            dbProgramIds.length > 0
        );
        // Ortak motor sonucu olmayan normal sınavı %0 gibi gösterme. Öntest
        // için hazır puanlı veri mevcutsa aşağıdaki puan-ağırlıklı yol sürer.
        if (selectedExamType && !hasDbProgramForSelected && !usingPrecomputed) {
            return [];
        }
        if (!hasDataForSelectedExam && !hasDbProgramForSelected && !(studentData?.programOutcomeResults?.length > 0)) {
            return [];
        }
        // Sınav bazlı DB PÇ yüzdeleri geldiyse onları doğrudan kullan
        if (
            selectedExamType &&
            Array.isArray(programOutcomes) &&
            programOutcomes.length > 0 &&
            Array.isArray(dbProgramIds) &&
            Array.isArray(dbProgramPercents) &&
            dbProgramIds.length > 0
        ) {
            return programOutcomes.map((outcome) => {
                const idx = dbProgramIds.indexOf(String(outcome.id));
                const rate = idx >= 0 ? Number(dbProgramPercents[idx]) : 0;
                return {
                    ...outcome,
                    correctCount: 0,
                    totalCount: 0,
                    successRate: Number.isFinite(rate) ? rate : 0,
                    relatedQuestions: []
                };
            });
        }
        // Genel görünümde ağırlıklı sınav birleşimi hazırlandıysa onu kullan
        if (
            !selectedExamType &&
            Array.isArray(programOutcomes) &&
            programOutcomes.length > 0 &&
            Array.isArray(aggregatedProgramPercents)
        ) {
            return programOutcomes
                .map((outcome, idx) => {
                    const rate = Number(aggregatedProgramPercents[idx]);
                    if (!Number.isFinite(rate)) return null;
                    return {
                        ...outcome,
                        correctCount: 0,
                        totalCount: 0,
                        successRate: rate,
                        relatedQuestions: []
                    };
                })
                .filter(Boolean);
        }
        // Genel görünümde ortak motor cevabı gelmeden yerel/legacy yüzde üretme.
        if (!selectedExamType) return [];

        // Eski hesaplama mantığı (fallback)
        if (!programOutcomes || !questions || !questionAnalysis) {
            return [];
        }
        return programOutcomes.map(outcome => {
            // Backend mantığına uygun: hem doğrudan program_outcomes hem de outcome_mappings kullanılır
            const allIndexesSet = new Set();
            
            // 1. Sorunun doğrudan program_outcomes'undan eşleşen soruları bul
            questions.forEach((q, idx) => {
                if (!selectedQuestionIndexSet.has(idx)) return;
                if (q.program_outcomes && (q.program_outcomes.includes(outcome.id) || q.program_outcomes.map(String).includes(String(outcome.id)))) {
                    allIndexesSet.add(idx);
                }
            });
            
            // 2. Sorunun learning_outcomes'undan outcome_mappings ile eşleşen soruları bul
            if (outcomeMappings && courseOutcomes) {
                questions.forEach((q, idx) => {
                    if (!selectedQuestionIndexSet.has(idx)) return;
                    const loIds = q.topics || q.learning_outcomes || [];
                    if (Array.isArray(loIds)) {
                        loIds.forEach(loId => {
                            const mappedPcs = outcomeMappings[String(loId)] || [];
                            if (mappedPcs.includes(outcome.id) || mappedPcs.map(String).includes(String(outcome.id))) {
                                allIndexesSet.add(idx);
                            }
                        });
                    }
                });
            }
            
            // 3. Set'i array'e çevir
            const allIndexes = Array.from(allIndexesSet);
            
            // 4. İlgili soruların analizini hazırla
            let relatedQuestions = allIndexes.map(idx => {
                const rawAns = answersRaw[idx];
                const ansStr = rawAns != null ? String(rawAns) : '';
                const isCorrect = Boolean(ansStr) && ansStr === ansStr.toUpperCase() && ansStr !== ansStr.toLowerCase();
                const q = questions[idx];
                // Soru puanını al
                const questionScore = Number(q?.score || 0) || (Number(pointsPerQuestionDisplay) || 0);
                return {
                    questionNumber: null,
                    studentAnswer: ansStr || 'Boş',
                    isCorrect,
                    questionText: q.text || '',
                    questionIndex: idx,
                    questionScore: questionScore
                };
            }).filter(Boolean).sort((a, b) => (a.questionIndex || 0) - (b.questionIndex || 0));
            // Numara dizisini 1..N olarak yeniden ver
            relatedQuestions = relatedQuestions.map((item, i) => ({
                ...item,
                questionNumber: i + 1
            }));
            // 5. Soru puanlarına göre başarı oranını hesapla
            let totalPoints = 0;
            let earnedPoints = 0;
            relatedQuestions.forEach(q => {
                const qScore = q.questionScore || 0;
                totalPoints += qScore;
                if (q.isCorrect) {
                    earnedPoints += qScore;
                }
            });
            const successRate = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
            
            // Doğru cevap sayısı ve toplam soru sayısı (geriye uyumluluk için)
            const correctCount = relatedQuestions.filter(q => q.isCorrect).length;
            const totalCount = relatedQuestions.length;
            return {
                ...outcome,
                correctCount,
                totalCount,
                successRate: parseFloat(successRate.toFixed(2)),
                relatedQuestions
            };
        });
    };

    const [dbLearningPercents, setDbLearningPercents] = React.useState(null);
    const [dbProgramPercents, setDbProgramPercents] = React.useState(null);
    const [dbLearningIds, setDbLearningIds] = React.useState(null);
    const [dbProgramIds, setDbProgramIds] = React.useState(null);
    const [aggregatedLearningPercents, setAggregatedLearningPercents] = React.useState(null);
    const [aggregatedProgramPercents, setAggregatedProgramPercents] = React.useState(null);
    const learningOutcomeResults = calculateLearningOutcomes();
    const programOutcomeResults = calculateProgramOutcomes();

    // DB'den ÖÇ yüzdelerini çek (sınav bazlı)
    React.useEffect(() => {
        try {
            setDbLearningPercents(null);
            setDbProgramPercents(null);
            setDbLearningIds(null);
            setDbProgramIds(null);
            if (!selectedExamType || !studentInfo || !courseId) return;
            const student_no = studentInfo[1] || '';
            const student_name = (studentInfo[2] || '').toString();
            const selWeight = Array.isArray(examWeights)
                ? examWeights.find(w => canonExamType(w?.type || w?.exam_type) === canonExamType(selectedExamType))
                : null;
            const selWeightId = selWeight?.id;
            const url = `${API_ENDPOINTS.STUDENT_EXAM_LEARNING}?course_id=${encodeURIComponent(courseId)}&exam_type=${encodeURIComponent(selectedExamType)}&student_no=${encodeURIComponent(student_no)}&student_name=${encodeURIComponent(student_name)}${programId ? `&program_id=${encodeURIComponent(programId)}` : ''}${selWeightId != null ? `&exam_weight_id=${encodeURIComponent(selWeightId)}` : ''}${selectedSpecificExamId ? `&selected_exam_id=${encodeURIComponent(selectedSpecificExamId)}` : ''}`;
            fetch(url)
                .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
                .then(({ ok, data }) => {
                    if (!ok) return;
                    const lIds = Array.isArray(data.learning_outcomes) ? data.learning_outcomes.map(x => String(x)) : [];
                    const lArr = Array.isArray(data.learning_percents) ? data.learning_percents.map(Number) : [];
                    setDbLearningIds(lIds);
                    setDbLearningPercents(lArr);
                    const pIds = Array.isArray(data.program_outcomes) ? data.program_outcomes.map(x => String(x)) : [];
                    const pArr = Array.isArray(data.program_percents) ? data.program_percents.map(Number) : [];
                    setDbProgramIds(pIds);
                    setDbProgramPercents(pArr);
                })
                .catch(() => {});
        } catch (_) {}
    }, [selectedExamType, selectedSpecificExamId, studentInfo, courseId, programId, examWeights, canonExamType]);

    // Tüm sınav türleri için ÖÇ/PÇ yüzdelerini çek ve ağırlıklarla genel yüzdeleri hesapla
    React.useEffect(() => {
        (async () => {
            try {
                // Kaldırıldı: setDbAllExamOutcomes
                setAggregatedLearningPercents(null);
                setAggregatedProgramPercents(null);
                const commonStudentNo = studentInfo?.[1] || '';
                if (courseId && commonStudentNo) {
                    const commonResponse = await fetch(
                        `${API_BASE_URL}/api/pre-midterm/term-final-student-breakdown?course_id=${encodeURIComponent(courseId)}&student_no=${encodeURIComponent(commonStudentNo)}`
                    );
                    const commonData = await commonResponse.json();
                    if (!commonResponse.ok) return;
                    const loById = new Map((commonData.learning_outcomes || []).map(item => [String(item.id), Number(item.pct)]));
                    const poById = new Map((commonData.program_outcomes || []).map(item => [String(item.id), Number(item.pct)]));
                    setAggregatedLearningPercents((courseOutcomes || []).map(item => loById.get(String(item.id))));
                    setAggregatedProgramPercents((programOutcomes || []).map(item => poById.get(String(item.id))));
                    return;
                }
                const weightedExamTypes = Array.isArray(examWeights)
                    ? examWeights
                        .map((w) => (w?.type || w?.exam_type || '').toString().trim())
                        .filter(Boolean)
                    : [];
                const sourceExamTypes = weightedExamTypes.length > 0 ? weightedExamTypes : (Array.isArray(examTypes) ? examTypes : []);
                if (sourceExamTypes.length === 0 || !courseId) return;
                const student_no = studentInfo?.[1] || '';
                const student_name = (studentInfo?.[2] || '').toString();
                const fetchOne = async (typeVal) => {
                    const w = Array.isArray(examWeights)
                        ? examWeights.find(x => canonExamType(x?.type || x?.exam_type) === canonExamType(typeVal))
                        : null;
                    const wid = w?.id;
                    const url = `${API_ENDPOINTS.STUDENT_EXAM_LEARNING}?course_id=${encodeURIComponent(courseId)}&exam_type=${encodeURIComponent(typeVal)}&student_no=${encodeURIComponent(student_no)}&student_name=${encodeURIComponent(student_name)}${programId ? `&program_id=${encodeURIComponent(programId)}` : ''}${wid != null ? `&exam_weight_id=${encodeURIComponent(wid)}` : ''}`;
                    const r = await fetch(url);
                    const d = await r.json();
                    if (!r.ok) return null;
                    return {
                        loIds: Array.isArray(d.learning_outcomes) ? d.learning_outcomes.map(x => String(x)) : [],
                        loPercs: Array.isArray(d.learning_percents) ? d.learning_percents.map(Number) : [],
                        poIds: Array.isArray(d.program_outcomes) ? d.program_outcomes.map(x => String(x)) : [],
                        poPercs: Array.isArray(d.program_percents) ? d.program_percents.map(Number) : [],
                    };
                };
                const results = await Promise.all(sourceExamTypes.map(t => fetchOne(t)));
                // Kanonik harita: sınav türlerini normalize et
                const map = {};
                sourceExamTypes.forEach((t, i) => {
                    if (!results[i]) return;
                    const key = canonExamType(t);
                    map[key] = results[i];
                });
                // Ağırlık haritası (kanonik)
                const weightsMap = {};
                (examWeights || []).forEach(w => {
                    const t = canonExamType(w.type || w.exam_type);
                    const wt = Number(w.weight || 0);
                    weightsMap[t] = wt;
                });
                const hasBut = Boolean(map['but']);
                // Aggregated learning per course outcome id
                if (Array.isArray(courseOutcomes) && courseOutcomes.length > 0) {
                    const agg = courseOutcomes.map(co => {
                        const idStr = String(co.id);
                        // Sadece bu ÖÇ'yi gerçekten ölçen (verisi olan) sınav türleri
                        // üzerinden ağırlıklı ortalama; yapılmamış sınavlar oranı ezmesin.
                        let total = 0, denom = 0;
                        Object.keys(map).forEach(t => {
                            // Bütünleme varsa final'i atla, 'but' için final ağırlığını kullan
                            if (hasBut && t === 'final') return;
                            const effectiveWeight = (hasBut && t === 'but') ? (Number(weightsMap['final'] || 0)) : Number(weightsMap[t] || 0);
                            if (effectiveWeight <= 0) return;
                            const rec = map[t];
                            const idx = rec.loIds.indexOf(idStr);
                            if (idx >= 0 && Number.isFinite(rec.loPercs[idx])) {
                                total += rec.loPercs[idx] * effectiveWeight;
                                denom += effectiveWeight;
                            }
                        });
                        return denom > 0 ? Number((total / denom).toFixed(2)) : 0;
                    });
                    setAggregatedLearningPercents(agg.some((v) => Number.isFinite(v)) ? agg : null);
                }
                // Aggregated program per program outcome id
                if (Array.isArray(programOutcomes) && programOutcomes.length > 0) {
                    const agg = programOutcomes.map(po => {
                        const idStr = String(po.id);
                        // Sadece bu PÇ'yi ölçen sınav türleri üzerinden ağırlıklı ortalama.
                        let total = 0, denom = 0;
                        Object.keys(map).forEach(t => {
                            if (hasBut && t === 'final') return;
                            const effectiveWeight = (hasBut && t === 'but') ? (Number(weightsMap['final'] || 0)) : Number(weightsMap[t] || 0);
                            if (effectiveWeight <= 0) return;
                            const rec = map[t];
                            const idx = rec.poIds.indexOf(idStr);
                            if (idx >= 0 && Number.isFinite(rec.poPercs[idx])) {
                                total += rec.poPercs[idx] * effectiveWeight;
                                denom += effectiveWeight;
                            }
                        });
                        return denom > 0 ? Number((total / denom).toFixed(2)) : 0;
                    });
                    setAggregatedProgramPercents(agg.some((v) => Number.isFinite(v)) ? agg : null);
                }
            } catch (_) {}
        })();
    }, [examTypes, courseId, programId, studentInfo, examWeights, courseOutcomes, programOutcomes, selectedExamType, canonExamType]);

    // (Kaldırıldı: kullanılmayan generalPCSuccess)
    
    // Eğer program çıktıları varsa ama results boşsa, fallback data oluştur
    // (Kaldırıldı: kullanılmayan displayProgramOutcomes)

    // (Kaldırıldı: kullanılmayan yardımcı fonksiyonlar)

    // Yeni: Bir ÖÇ için PÇ analizini getir
    // (Kaldırıldı: kullanılmayan fetchProgramOutcomesForOutcome)

    return (!isOpen ? null : (
        <div className="student-modal-overlay" onClick={onClose}>
            <div className="student-modal-content" onClick={e => e.stopPropagation()}>
                <div className="student-modal-header">
                    <h2>Öğrenci Detay Raporu</h2>
                    <button className="student-modal-close" onClick={onClose} aria-label="Kapat">
                        ×
                    </button>
                </div>

                <div className="student-modal-body">
                    {/* Öğrenci Bilgi Başlığı - Scroll'a Duyarlı */}
                    <div className={`student-header-compact ${isScrolled ? 'scrolled' : 'normal'}`}>
                        <div className="student-avatar">
                            <i className="fas fa-user-graduate"></i>
                        </div>
                        <div className="student-basic-info">
                            <h3>{studentInfo[2] || studentInfo.name || 'Öğrenci Adı'}</h3>
                            {!isScrolled && (
                                <div className="student-details">
                                    <p className="student-number">Öğrenci No: {studentInfo[1] || studentInfo.student_no || 'N/A'} • Sınav Türü: {selectedExamType || 'Genel'}</p>

                                </div>
                            )}
                            {/* Öğrencinin girdiği sınav türleri - tıklanabilir chip'ler */}
                            {Array.isArray(examTypes) && examTypes.length > 0 && (
                                <div className="exam-type-chips">
                                    {examTypes.map((t, idx) => {
                                        const isSel = String(t) === String(selectedExamType);
                                        return (
                                            <button
                                                key={`${t}-${idx}`}
                                                type="button"
                                                className={`exam-chip ${isSel ? 'selected' : ''}`}
                                                onClick={() => handleToggleExam(t)}
                                            >
                                                {t}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {/* Sınav türü seçimi kaldırıldı - seçim kartlardan yapılır */}
                        <div className="quick-success-rate" style={{ display: 'flex', gap: 12 }}>
                            {selectedExamType && (
                                <div className="exam-success">
                                    <span className="success-percentage">{formatScoreDisplay(examScore)}</span>
                                    {!isScrolled && <span className="success-label">Sınav Notu ({selectedExamType})</span>}
                                </div>
                            )}
                            <div className="general-success">
                                <span className="success-percentage">{formatScoreDisplay(generalScore)}</span>
                                {!isScrolled && <span className="success-label">Genel Not</span>}
                            </div>
                        </div>
                        {isScrolled && (
                            <div 
                                className="scroll-indicator"
                                onClick={() => {
                                    const modalBody = document.querySelector('.student-modal-body');
                                    if (modalBody) {
                                        modalBody.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                }}
                                title="Başa dön"
                            >
                                <i className="fas fa-chevron-up"></i>
                            </div>
                        )}
                    </div>

                    {/* Sınav Ağırlıkları Bilgisi */}
                    {Array.isArray(examWeights) && examWeights.length > 0 && (
                        <div className="section-card">
                            <div className="section-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                    <h4>
                                        <i className="fas fa-balance-scale"></i>
                                        Sınav Ağırlıkları
                                    </h4>
                                    <span className="section-subtitle">Seçili: {selectedExamType || 'Genel'}</span>
                                </div>
                                {!!selectedExamType && (
                                    <button type="button" className="exam-chip" onClick={() => handleToggleExam('')}>Genel</button>
                                )}
                            </div>
                            <div className="exam-weights-display">
                                <div className="weights-grid">
                                    {/* Genel (tümü) seçeneği - en solda sabit */}
                                    <div
                                        className={`weight-item general ${!selectedExamType ? 'selected' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleToggleExam('')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleToggleExam('');
                                            }
                                        }}
                                    >
                                        <div className="weight-type">Genel Not</div>
                                    </div>

                                    {examWeights.map((weight, index) => {
                                        const wType = weight.type || weight.exam_type;
                                        const isSelected = wType === selectedExamType;
                                        return (
                                            <div
                                                key={index}
                                                className={`weight-item ${isSelected ? 'selected' : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleToggleExam(wType)}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Enter' || e.key === ' ')) {
                                                        e.preventDefault();
                                                        handleToggleExam(wType);
                                                    }
                                                }}
                                            >
                                                <div className="weight-type">{wType}</div>
                                                {weight.weight != null && weight.weight !== '' && (
                                                    <div className="weight-value">%{weight.weight}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="weight-info">
                                    <p>
                                        <strong>Seçili sınav ağırlığı:</strong> %{getSelectedExamWeight()}
                                    </p>
                                    {selectedExamType && specificExamOptions.length > 1 && (
                                        <div className="specific-exam-selector">
                                            <div className="specific-exam-label">Sınav seç:</div>
                                            <div className="specific-exam-chips" role="tablist" aria-label="Sınav seçimi">
                                                <button
                                                    type="button"
                                                    className={`specific-exam-chip ${!selectedSpecificExamId ? 'selected' : ''}`}
                                                    onClick={() => setSelectedSpecificExamId('')}
                                                >
                                                    Genel
                                                </button>
                                                {specificExamOptions.map((exam, idx) => {
                                                    const examId = String(exam.exam_id);
                                                    const isSelected = String(selectedSpecificExamId) === examId;
                                                    return (
                                                        <button
                                                            key={exam.exam_id}
                                                            type="button"
                                                            className={`specific-exam-chip ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => setSelectedSpecificExamId(examId)}
                                                            title={exam.title || `Sınav ${exam.exam_id}`}
                                                        >
                                                            {`${selectedExamType} ${idx + 1}`}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}



                    {/* Dersin öğrenme çıktıları Detaylı Analizi */}
                    {learningOutcomeResults && learningOutcomeResults.length > 0 ? (
                        <div className="section-card">
                            <div className="section-header">
                                <h4>
                                    <i className="fas fa-graduation-cap"></i>
                                    Dersin öğrenme çıktıları Analizi
                                </h4>
                            </div>
                            
                            <div className="outcomes-compact-table">
                                <table className="learning-outcomes-table">
                                    <colgroup>
                                        <col className="col-outcome-code" />
                                        <col className="col-outcome-desc" />
                                        <col className="col-outcome-rate" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>ÖÇ</th>
                                            <th>Açıklama</th>
                                            <th>Başarı Oranı</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {learningOutcomeResults.map((outcome, idx) => (
                                            <React.Fragment key={idx}>
                                            <tr className={outcome.successRate >= 60 ? 'success-outcome' : 'failure-outcome'}>
                                                <td className="outcome-code-compact">{outcome.code}</td>
                                                <td className="outcome-desc-compact">{outcome.description}</td>
                                                <td className="outcome-success-compact">
                                                    <div className="success-indicator">
                                                        <span className="success-percent">%{Number(toDisplayOutcomeRate(outcome.successRate)).toFixed(2)}</span>
                                                        <div className="success-track">
                                                            <div
                                                                className="success-fill"
                                                                style={{ width: `${Math.max(0, Math.min(100, Number(toDisplayOutcomeRate(outcome.successRate)) || 0))}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* PÇ detay sütunu kaldırıldı */}
                                            </tr>
                                            {/* PÇ detay satırı kaldırıldı */}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="section-card">
                            <div className="section-header">
                                <h4>
                                    <i className="fas fa-graduation-cap"></i>
                                    Dersin öğrenme çıktıları Analizi
                                </h4>
                            </div>
                            <div className="no-data-message">
                                {selectedExamType
                                    ? 'Bu sınav için ÖÇ analizi bulunamadı (sadece not verisi mevcut olabilir).'
                                    : 'ÖÇ analizi bulunamadı.'}
                            </div>
                        </div>
                    )}
                    {/* PÇ KOMPAKT TABLOSU */}
                    {programOutcomeResults && programOutcomeResults.length > 0 && (
                        <div className="section-card pc-compact-table">
                            <div className="section-header">
                                <h4>
                                    <i className="fas fa-cubes"></i>
                                    Program Çıktıları Analizi
                                </h4>
                            </div>
                            <div className="outcomes-compact-table">
                                <table className="learning-outcomes-table">
                                    <colgroup>
                                        <col className="col-outcome-code" />
                                        <col className="col-outcome-desc" />
                                        <col className="col-outcome-rate" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>PÇ</th>
                                            <th>Açıklama</th>
                                            <th>Başarı Oranı</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {programOutcomeResults.map((po, idx) => (
                                            <tr key={po.id || idx} className={po.successRate >= 60 ? 'success-outcome' : 'failure-outcome'}>
                                                <td className="outcome-code-compact">{po.code || po.number || po.id}</td>
                                                <td className="outcome-desc-compact">{po.description}</td>
                                                <td className="outcome-success-compact">
                                                    <div className="success-indicator">
                                                        <span className="success-percent">%{Number(toDisplayOutcomeRate(po.successRate)).toFixed(2)}</span>
                                                        <div className="success-track">
                                                            <div
                                                                className="success-fill"
                                                                style={{ width: `${Math.max(0, Math.min(100, Number(toDisplayOutcomeRate(po.successRate)) || 0))}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {/* PROGRAM OUTCOMES ID TABLOSU */}
                    {/* Bu tabloyu kaldırıyoruz */}
                    {/* {programOutcomes && programOutcomes.length > 0 && (
                        <div className="section-card program-c-id-list">
                            <div className="section-header">
                                <h4>
                                    <i className="fas fa-list-ol"></i>
                                    Program Çıktıları (ID Listesi)
                                </h4>
                            </div>
                            <div className="table-responsive">
                                <table className="compact-table">
                                    <thead>
                                        <tr>
                                            <th>PÇ ID</th>
                                            <th>Kod</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {programOutcomes.map((po, idx) => (
                                            <tr key={po.id || idx}>
                                                <td>{po.id}</td>
                                                <td>{po.code || po.number || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )} */}
                    {/* PROGRAMIN GENEL ÇIKTILARI TABLOSU */}
                    {/* Bu tabloyu kaldırıyoruz */}
                    {/* {programOutcomes && programOutcomes.length > 0 && (
                        <div className="section-card program-c-list">
                            ...
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    ));
};

export default StudentDetailModal; 
